import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { api, type CurrentUser } from '../api';
import { connectApi, type GrantView } from '../api/connect';
import { meApi, type TelegramLink, type TelegramLinkCode } from '../api/me';
import { t } from '../i18n';
import './profile.css';

export function Profile({ user, onUserChange }: { user: CurrentUser; onUserChange: (u: CurrentUser) => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauth: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [telegram, setTelegram] = useState<TelegramLink | null>(user.telegram ?? null);
  const [linkCode, setLinkCode] = useState<TelegramLinkCode | null>(null);
  const [telegramError, setTelegramError] = useState(false);
  const [grants, setGrants] = useState<GrantView[] | null>(null);
  const [grantsError, setGrantsError] = useState(false);

  useEffect(() => {
    setSetup(null);
    setRecovery(null);
  }, [user.twoFactorEnabled]);

  // the login response carries no link state, so the page asks `/me` for it once
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((u) => {
        if (!cancelled) setTelegram(u.telegram ?? { linked: false });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const start = async () => {
    setError(null);
    const s = await api.setup2fa();
    const qr = await QRCode.toDataURL(s.otpauth, { margin: 0, width: 360 });
    setSetup({ ...s, qr });
  };

  const enable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.enable2fa(code);
      setRecovery(r.recoveryCodes);
      setSetup(null);
      setCode('');
      onUserChange(await api.me());
    } catch {
      setError(t('auth.2fa.error'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.disable2fa(code);
      setCode('');
      onUserChange(await api.me());
    } catch {
      setError(t('auth.2fa.error'));
    } finally {
      setBusy(false);
    }
  };

  // connected assistants (ADR-0006): the grants this human gave through the consent page
  useEffect(() => {
    let cancelled = false;
    connectApi
      .grants()
      .then((rows) => {
        if (!cancelled) setGrants(rows);
      })
      .catch(() => {
        if (!cancelled) setGrantsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const revokeGrant = async (id: string) => {
    setGrantsError(false);
    try {
      setGrants(await connectApi.revokeGrant(id));
    } catch {
      setGrantsError(true);
    }
  };

  const getLinkCode = async () => {
    setTelegramError(false);
    try {
      setLinkCode(await meApi.telegramLinkCode());
    } catch {
      setTelegramError(true);
    }
  };

  const unlink = async () => {
    setTelegramError(false);
    try {
      setTelegram(await meApi.unlinkTelegram());
      setLinkCode(null);
    } catch {
      setTelegramError(true);
    }
  };

  /** The code's expiry as a wall clock time in the browser's own zone. */
  const expiryTime = (iso: string): string => new Date(iso).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <h1>{t('profile.title')}</h1>
      <div className="card stack">
        <div>
          <strong>{user.displayName}</strong> <span className="muted">{user.email}</span>
        </div>
        <div className="muted">{user.roles.join(', ')}</div>
      </div>

      <h2>{t('profile.2fa')}</h2>
      <div className="card stack">
        <div className="row">
          <span className="badge">{t(user.twoFactorEnabled ? 'profile.2fa.on' : 'profile.2fa.off')}</span>
          {!user.twoFactorEnabled && !setup && !recovery && (
            <button className="btn secondary small" onClick={start}>{t('profile.2fa.start')}</button>
          )}
        </div>

        {recovery && (
          <div className="stack">
            <div className="muted">{t('profile.2fa.recovery')}</div>
            <div className="codes">
              {recovery.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <div>
              <button className="btn secondary small" onClick={() => setRecovery(null)}>{t('common.close')}</button>
            </div>
          </div>
        )}

        {setup && (
          <form className="form" onSubmit={enable}>
            <div className="muted">{t('profile.2fa.scan')}</div>
            <img className="qr" src={setup.qr} alt="QR" />
            <div className="field">
              {t('profile.2fa.secret')}
              <code className="mono">{setup.secret}</code>
            </div>
            <label>
              {t('profile.2fa.confirm')}
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required style={{ maxWidth: 200 }} />
            </label>
            {error && <div className="error">{error}</div>}
            <div className="row">
              <button className="btn" type="submit" disabled={busy}>{t('profile.2fa.enable')}</button>
              <button className="btn secondary" type="button" onClick={() => setSetup(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {user.twoFactorEnabled && !recovery && (
          <form className="form" onSubmit={disable}>
            <div className="muted">{t('profile.2fa.disableHint')}</div>
            <label>
              {t('profile.2fa.confirm')}
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required style={{ maxWidth: 200 }} />
            </label>
            {error && <div className="error">{error}</div>}
            <div>
              <button className="btn danger" type="submit" disabled={busy}>{t('profile.2fa.disable')}</button>
            </div>
          </form>
        )}
      </div>

      <h2>{t('profile.telegram')}</h2>
      <div className="card stack">
        <p className="muted">{t('profile.telegram.intro')}</p>

        {telegram?.linked ? (
          <div className="row between">
            <span>{t('profile.telegram.linked', { suffix: telegram.chatIdSuffix ?? '' })}</span>
            <button className="btn secondary small" onClick={unlink}>{t('profile.telegram.unlink')}</button>
          </div>
        ) : (
          <div className="row">
            <span className="badge">{t('profile.telegram.notLinked')}</span>
            <button className="btn secondary small" onClick={getLinkCode}>{t(linkCode ? 'profile.telegram.newCode' : 'profile.telegram.getCode')}</button>
          </div>
        )}

        {linkCode && !telegram?.linked && (
          <>
            <div className="row">
              <span className="link-code">{linkCode.code}</span>
              <span className="muted">{t('profile.telegram.expiresAt', { time: expiryTime(linkCode.expiresAt) })}</span>
            </div>
            <p className="muted">
              {t('profile.telegram.howto')} <span className="mono link-command">/link {linkCode.code}</span>
            </p>
          </>
        )}

        {telegramError && <div className="error">{t('profile.telegram.error')}</div>}
      </div>

      <h2>{t('profile.assistants')}</h2>
      <div className="card stack">
        <p className="muted">{t('profile.assistants.intro')}</p>
        {grants === null && !grantsError && <div className="muted">{t('common.loading')}</div>}
        {grants && grants.length === 0 && <span className="badge">{t('profile.assistants.none')}</span>}
        {grants &&
          grants.map((g) => (
            <div className="row between" key={g.id}>
              <span>
                <strong>{g.client.name}</strong>{' '}
                <span className="muted">
                  {t('profile.assistants.connected', { date: shortDate(g.createdAt) })} · {t('profile.assistants.lastUsed', { date: shortDate(g.lastUsedAt) })}
                </span>
              </span>
              <button className="btn secondary small" onClick={() => void revokeGrant(g.id)}>{t('profile.assistants.revoke')}</button>
            </div>
          ))}
        <p className="muted">{t('profile.assistants.howto', { url: `${window.location.origin}/api/mcp` })}</p>
        {grantsError && <div className="error">{t('common.error')}</div>}
      </div>
    </>
  );
}

/** A grant's dates as day and time in the browser's own zone; the list is short, so no table. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
